import { ActionExpression, FunctionUtils, ReflectUtils } from "@dooboostore/core";
import { EventListenerLifeCycler } from "./addEventListener.js";
import { MutationObserverLifeCycler } from "./mutationObserver.js";
import { ResizeObserverLifeCycler } from "./resizeObserver.js";
import { IntersectionObserverLifeCycler } from "./intersectionObserver.js";
import { findAllLifecycleMetadata, findAllOnConnectedAfterMetadata, findAllOnConnectedBeforeMetadata, findAllOnConnectedMetadata, ON_AFTER_ADOPTED_METADATA_KEY, ON_AFTER_DISCONNECTED_METADATA_KEY, ON_BEFORE_ADOPTED_METADATA_KEY, ON_BEFORE_DISCONNECTED_METADATA_KEY, ON_CONNECTED_COMPLETED_METADATA_KEY, ON_INITIALIZE_METADATA_KEY } from "./lifecycles.js";
import { EmitCustomEventLifeCycler } from "./emitCustomEvent.js";
import { ChangedAttributeLifeCycler } from "./changedAttribute.js";
import { findAllAttributeApplyMetadata, findAllAttributeMetadata } from "./applyAttribute.js";
import { getQueryMetadata, getQueryAllMetadata } from "./query.js";
import { SwcUtils } from "../utils/Utils.js";
import { DOM_EVENT_NAMES, HTML_TAG_ENTRIES } from "../config/config.js";
import { SituationTypeContainer, SituationTypeContainers } from "@dooboostore/simple-boot/decorators/inject/Inject";
import { buildSwcParameterArgs, buildDomStorageKindValues, getParameterMetadata } from "./parameter.js";
import { InjectSituationType } from "../types/index.js";
import { ConvertUtils, ElementApply } from "@dooboostore/core-web";
import { isSSR } from "../elements/SwcAppMixin.js";
import { findAllStateMetadata } from "./state.js";
import { findAllAroundMetadata } from "./around.js";
import { findAllPropertyMetadata } from "./applyProperty.js";
import { MessageSubscribeLifeCycler } from "./subscribeSwcAppMessage.js";
import { RouteSubscribeLifeCycler } from "./subscribeSwcAppRouteChange.js";
import { SetIntervalLifeCycler } from "./setInterval.js";
import { SetTimeoutLifeCycler } from "./setTimeout.js";
import { RequestAnimationFrameLifeCycler } from "./requestAnimationFrame.js";
import { EventMediaLifeCycler } from "./eventMedia.js";
import { FetchLifeCycler, assertFetchFields } from "./fetch.js";
const ELEMENT_CONFIG_KEY = Symbol.for("simple-web-component:element-config");
const getElementConfig = (target) => {
  const constructor = typeof target === "function" ? target : target.constructor;
  return ReflectUtils.getMetadata(ELEMENT_CONFIG_KEY, constructor);
};
const ensureInit = (inst) => {
  if (!inst.__swc_initialized) {
    inst._swcId = inst?.getAttribute?.("swc-use-ssr") ?? "s" + Math.random().toString(36).substring(2, 11).toLowerCase();
    inst._emitHandlers = /* @__PURE__ */ new Map();
    inst.__swc_initialized = true;
    const target = typeof inst === "function" ? inst : inst.constructor;
    const attributeAllList = findAllAttributeMetadata(target).filter((it) => it.type === "property");
    if (attributeAllList) {
      attributeAllList.forEach((meta) => {
        delete inst[meta.propertyKey];
      });
    }
    const queryList = getQueryMetadata(target);
    if (queryList) {
      queryList.forEach((meta) => {
        delete inst[meta.propertyKey];
      });
    }
    const queryAllList = getQueryAllMetadata(target);
    if (queryAllList) {
      queryAllList.forEach((meta) => {
        delete inst[meta.propertyKey];
      });
    }
    const propertyAllList = findAllPropertyMetadata(target).filter((it) => it.type === "property");
    if (propertyAllList) {
      propertyAllList.forEach((meta) => {
        let proto = Object.getPrototypeOf(inst);
        let hasGetter = false;
        while (proto && proto !== Object.prototype) {
          const d = Object.getOwnPropertyDescriptor(proto, meta.propertyKey);
          if (d) {
            hasGetter = typeof d.get === "function";
            break;
          }
          proto = Object.getPrototypeOf(proto);
        }
        if (!hasGetter) return;
        delete inst[meta.propertyKey];
      });
    }
    const stateAllList = findAllStateMetadata(target);
    if (stateAllList) {
      stateAllList.forEach((meta) => {
        const own = Object.getOwnPropertyDescriptor(inst, meta.propertyKey);
        if (own && "value" in own) {
          const initUserData = own.value;
          delete inst[meta.propertyKey];
          inst[meta.propertyKey] = initUserData;
        }
      });
    }
    const aroundAllList = findAllAroundMetadata(target);
    if (aroundAllList) {
      aroundAllList.forEach((meta) => {
        const own = Object.getOwnPropertyDescriptor(inst, meta.propertyKey);
        if (own && "value" in own) {
          const initUserData = own.value;
          delete inst[meta.propertyKey];
          inst[meta.propertyKey] = initUserData;
        }
      });
    }
    const hostSet = SwcUtils.getHostSet(inst);
    const appHosts = SwcUtils.findAllAppHostsIncludingSelfDirect(inst);
    inst._executeSwcScript?.("swc-on-constructor", hostSet);
    const cMethods = findAllLifecycleMetadata(inst, ON_INITIALIZE_METADATA_KEY);
    for (const m of cMethods) inst._invokeLifecycleMethod(m.propertyKey, hostSet);
  }
};
const globalDelegatedRoots = /* @__PURE__ */ new WeakSet();
const handleGlobalSwcEvent = async (event) => {
  if (event.__swc_handled) return;
  const path = event.composedPath();
  const type = event.type;
  const attrName = `swc-on-${type}`;
  for (const node of path) {
    if (!(node instanceof HTMLElement)) continue;
    const script = node.getAttribute?.(attrName);
    if (script && !getElementConfig(node)) {
      const host = SwcUtils.findNearestSwcAncestorHost(node);
      if (host && host.isConnected && typeof host.__swc_executeAttributeEvent === "function") {
        await host.__swc_executeAttributeEvent(node, attrName, script, event);
        event.__swc_handled = true;
        break;
      }
    }
  }
};
function buildEnv(configWindow) {
  const win = configWindow;
  const doc = win?.document;
  const builtInTagMap = /* @__PURE__ */ new Map();
  for (const [cls, tag] of HTML_TAG_ENTRIES) {
    if (win?.[cls]) builtInTagMap.set(win[cls], tag);
  }
  const domHelpers = SwcUtils.getHelperSet(win);
  return { win, doc, builtInTagMap, domHelpers };
}
const getHandlers = (inst) => {
  if (!inst.__swc_attributeEventHandlers) inst.__swc_attributeEventHandlers = /* @__PURE__ */ new Map();
  return inst.__swc_attributeEventHandlers;
};
const setupPrototype = (proto, win) => {
  if (proto.__swc_proto_setup) return;
  proto.__swc_proto_setup = true;
  proto._executeSwcScript = function(attrName, hostSet, extraArgs = {}) {
    ensureInit(this);
    const script = this.getAttribute(attrName);
    if (script) {
      try {
        const currentWin = SwcUtils.resolveWindow(this);
        const helpers = SwcUtils.getHelperSet(currentWin);
        const args = { ...hostSet, ...helpers, ...extraArgs, $el: this, $root: this.getRootNode() };
        FunctionUtils.execute({ script, context: this, args });
      } catch (e) {
        console.error(`[SWC] Failed to execute ${attrName}:`, e);
      }
    }
  };
  proto._invokeLifecycleMethod = function(methodName, hostSet, extraArgs = []) {
    ensureInit(this);
    if (typeof this[methodName] !== "function") return;
    const useHostSet = hostSet ?? SwcUtils.getHelperAndHostSet(this);
    const app = useHostSet?.$appHost?.simpleApplication;
    const helperSetValue = SwcUtils.getHelperSet(win);
    const helperHostSetValue = { ...helperSetValue, ...useHostSet, $this: this };
    const kindValues = {
      hostSet: useHostSet,
      helperHostSet: helperHostSetValue,
      helperSet: helperSetValue,
      swcAppRouter: useHostSet?.$appHost?.router,
      swcAppSimpleApplication: useHostSet?.$appHost?.simpleApplication,
      swcAppHost: useHostSet?.$appHost,
      ...buildDomStorageKindValues(this, win)
    };
    if (app) {
      const otherStorage = /* @__PURE__ */ new Map();
      const situations = new SituationTypeContainers([new SituationTypeContainer({ situationType: InjectSituationType.HOST_SET, data: useHostSet }), new SituationTypeContainer({ situationType: InjectSituationType.APP_HOST, data: useHostSet.$appHost }), new SituationTypeContainer({ situationType: InjectSituationType.APP_HOSTS, data: useHostSet.$appHosts }), new SituationTypeContainer({
        situationType: InjectSituationType.HOST,
        data: useHostSet.$host
      }), new SituationTypeContainer({ situationType: InjectSituationType.HOSTS, data: useHostSet.$hosts }), new SituationTypeContainer({ situationType: InjectSituationType.FIRST_HOST, data: useHostSet.$firstHost }), new SituationTypeContainer({ situationType: InjectSituationType.LAST_HOST, data: useHostSet.$lastHost }), new SituationTypeContainer({
        situationType: InjectSituationType.FIRST_APP_HOST,
        data: useHostSet.$firstAppHost
      }), new SituationTypeContainer({ situationType: InjectSituationType.LAST_APP_HOST, data: useHostSet.$lastAppHost })]);
      otherStorage.set(SituationTypeContainers, situations);
      const firstCheckMaker = ({ target, targetKey }, token, idx) => {
        const saves = getParameterMetadata(target, targetKey);
        const found = saves.find((s) => s.index === idx);
        if (!found) return void 0;
        const value = kindValues[found.kind];
        return (found.key !== void 0 && typeof value === "function" ? value(found.key) : value) ?? null;
      };
      return app.simstanceManager.executeBindParameterSim(
        {
          target: this,
          targetKey: methodName,
          inputParameters: extraArgs,
          firstCheckMaker: [firstCheckMaker]
        },
        otherStorage
      );
    } else {
      const args = buildSwcParameterArgs(this, methodName, kindValues, extraArgs);
      return this[methodName](...args);
    }
  };
  proto._bindAttributeEvent = function(el, attrName, script, eventName) {
    ensureInit(this);
    if (!eventName) {
      if (!attrName.startsWith("swc-on-")) return;
      eventName = attrName.substring(7);
    }
    const elHandlers = getHandlers(this);
    let handlers = elHandlers.get(el);
    if (!handlers) {
      handlers = /* @__PURE__ */ new Map();
      elHandlers.set(el, handlers);
    }
    const oldHandler = handlers.get(attrName);
    if (oldHandler) el.removeEventListener(eventName, oldHandler);
    const handler = async (event) => {
      const hostSet = SwcUtils.getHostSet(el);
      const currentWin = SwcUtils.resolveWindow(this);
      const helpers = SwcUtils.getHelperSet(currentWin);
      const args = {
        event,
        $data: event.detail,
        ...hostSet,
        ...helpers,
        $el: el,
        $root: this.getRootNode()
      };
      await FunctionUtils.execute({ script, context: el, args });
    };
    el.addEventListener(eventName, handler);
    handlers.set(attrName, handler);
  };
  proto.__swc_executeAttributeEvent = async function(el, attrName, script, event) {
    ensureInit(this);
    const hostSet = SwcUtils.getHostSet(el);
    const currentWin = SwcUtils.resolveWindow(this);
    const currentHelpers = SwcUtils.getHelperSet(currentWin);
    const detail = event.detail;
    const args = {
      event,
      $event: event,
      $data: detail,
      $detail: detail,
      ...hostSet,
      ...currentHelpers,
      $el: el,
      $root: this.getRootNode()
    };
    await FunctionUtils.execute({ script, context: el, args });
  };
};
const SWC_ORIGINAL_CALLBACK = Symbol.for("simple-web-component:original-callback");
const userCallback = (fn) => typeof fn === "function" && SWC_ORIGINAL_CALLBACK in fn ? fn[SWC_ORIGINAL_CALLBACK] : fn;
const elementDefine = (name, config = {}) => (constructor) => {
  const { win, doc, builtInTagMap: BUILT_IN_TAG_MAP, domHelpers: SWC_DOM_HELPERS } = buildEnv(config.window);
  const metadata = { ...config, name, window: win };
  if (!metadata.window) {
    throw new Error("window is required");
  }
  assertFetchFields(constructor);
  let extendsTagName = metadata.extends;
  if (!extendsTagName) {
    let proto2 = constructor;
    const BaseHTMLElement = win.HTMLElement;
    while (proto2 && proto2 !== BaseHTMLElement && proto2 !== Function.prototype) {
      extendsTagName = BUILT_IN_TAG_MAP.get(proto2);
      if (extendsTagName) break;
      proto2 = Object.getPrototypeOf(proto2);
    }
    metadata.extends = extendsTagName;
  }
  const attributeList = findAllAttributeMetadata(constructor);
  const applyAttributeMap = findAllAttributeApplyMetadata(constructor);
  const swcLifecycleAttributes = ["swc-on-constructor", "swc-on-connected", "swc-on-disconnected", "swc-on-before-connected", "swc-on-after-connected", "swc-on-before-disconnected", "swc-on-after-disconnected", "swc-on-before-adopted", "swc-on-after-adopted", "swc-on-attribute-changed"];
  const swcOnEvents = DOM_EVENT_NAMES.map((e) => `swc-on-${e}`);
  const attributeApplyNames = Array.from(applyAttributeMap.values()).map((it) => it.targetAttributeName).filter(Boolean);
  const hostAttributes = attributeList.filter((it) => it.selector === "$this").map((it) => it.propertyKey || String(it.propertyKey));
  const originalStaticObservedAttributes = constructor.observedAttributes ?? [];
  const proto = constructor.prototype;
  setupPrototype(proto, win);
  const connectedHelperHostSet = (inst) => inst.__swc_helperHostSet ?? SwcUtils.getHelperAndHostSet(inst);
  const eventCycler = new EventListenerLifeCycler();
  const resizeCycler = new ResizeObserverLifeCycler();
  const intersectionCycler = new IntersectionObserverLifeCycler();
  const mutationCycler = new MutationObserverLifeCycler();
  const cyclers = [
    eventCycler,
    new ChangedAttributeLifeCycler(),
    new EmitCustomEventLifeCycler(),
    resizeCycler,
    intersectionCycler,
    mutationCycler,
    new MessageSubscribeLifeCycler(),
    new RouteSubscribeLifeCycler(),
    new SetIntervalLifeCycler(),
    new SetTimeoutLifeCycler(),
    new RequestAnimationFrameLifeCycler(),
    new EventMediaLifeCycler(),
    new FetchLifeCycler()
  ];
  const cyclerObservedAttributes = cyclers.flatMap((c) => c.getObservedAttributeNames?.(constructor) ?? []);
  const mergedObservedAttributes = [.../* @__PURE__ */ new Set([...metadata.observedAttributes ?? [], ...originalStaticObservedAttributes, ...cyclerObservedAttributes, ...attributeApplyNames, ...hostAttributes, ...swcLifecycleAttributes, ...swcOnEvents])];
  const newObserverState = () => ({
    observers: [],
    roCallbacks: /* @__PURE__ */ new Set(),
    roObserved: /* @__PURE__ */ new WeakSet(),
    resizeDelegates: [],
    intersectionDelegates: [],
    moCallbacks: /* @__PURE__ */ new Set(),
    moTargets: /* @__PURE__ */ new Map()
  });
  const mergeMutationInit = (existing, next) => {
    if (!existing) return next;
    const mergedFilter = existing.attributeFilter || next.attributeFilter ? [.../* @__PURE__ */ new Set([...existing.attributeFilter ?? [], ...next.attributeFilter ?? []])] : void 0;
    return {
      childList: existing.childList || next.childList,
      attributes: existing.attributes || next.attributes,
      characterData: existing.characterData || next.characterData,
      subtree: existing.subtree || next.subtree,
      attributeOldValue: existing.attributeOldValue || next.attributeOldValue,
      characterDataOldValue: existing.characterDataOldValue || next.characterDataOldValue,
      attributeFilter: mergedFilter
    };
  };
  const forDelegateMatches = (n, selector, fn) => {
    if (n.nodeType !== 1) return;
    const el = n;
    const isThis = selector === "$this" || selector === "";
    if (isThis || el.matches?.(selector) || el.closest?.(selector)) fn(el);
    else el.querySelectorAll(selector).forEach(fn);
  };
  const buildObservers = async (helperHostSet, scope, st, alive) => {
    const inst = helperHostSet.$this;
    const activeCyclers = scope === "self" ? [resizeCycler, mutationCycler] : cyclers;
    const resolveDelegateRoots = (delegateRoot) => {
      const r = delegateRoot || "auto";
      const roots = [];
      if (r === "auto") roots.push(inst.shadowRoot || inst);
      else if (r === "light") roots.push(inst);
      else if (r === "shadow" && inst.shadowRoot) roots.push(inst.shadowRoot);
      else if (r === "all") {
        roots.push(inst);
        if (inst.shadowRoot) roots.push(inst.shadowRoot);
      }
      return roots;
    };
    let resizeObserverSet = [];
    let mutationObserverSet = [];
    let intersectionObserverSet = [];
    for (const c of activeCyclers) {
      const result = await c.onConnected?.(helperHostSet, resizeObserverSet, scope);
      if (!alive()) return;
      if (!result) continue;
      if (result.resizeObserverSet?.length) resizeObserverSet = [...resizeObserverSet, ...result.resizeObserverSet];
      if (result.mutationObserverSet?.length) mutationObserverSet = [...mutationObserverSet, ...result.mutationObserverSet];
      if (result.intersectionObserverSet?.length) intersectionObserverSet = [...intersectionObserverSet, ...result.intersectionObserverSet];
    }
    const resizeObserve = (el, opts) => {
      if (!st.ro || st.roObserved.has(el)) return;
      st.ro.observe(el, opts);
      st.roObserved.add(el);
    };
    const resizeUnobserve = (el) => {
      if (!st.ro || !st.roObserved.has(el)) return;
      st.ro.unobserve(el);
      st.roObserved.delete(el);
    };
    const newResizeDelegates = [];
    if (resizeObserverSet.length) {
      if (!st.ro) {
        st.ro = new win.ResizeObserver((entries, obs) => {
          for (const cb of st.roCallbacks) cb(entries, obs);
        });
        st.observers.push(st.ro);
      }
      for (const e of resizeObserverSet) {
        st.roCallbacks.add(e.callback);
        if (e.delegate) {
          if (typeof e.target === "string") newResizeDelegates.push(e);
          continue;
        }
        if (typeof e.target === "string") {
          for (const r of resolveDelegateRoots(e.delegateRoot)) r.querySelectorAll(e.target).forEach((el) => resizeObserve(el, e.options));
          continue;
        }
        st.ro.observe(e.target, e.options);
        if (e.target instanceof Element) st.roObserved.add(e.target);
      }
      st.resizeDelegates.push(...newResizeDelegates);
    }
    const newIntersectionDelegates = [];
    for (const group of intersectionObserverSet) {
      const ioCallbacks = [...new Set(group.observeTargets.map((e) => e.callback))];
      const io = new win.IntersectionObserver((entries, obs) => {
        for (const cb of ioCallbacks) cb(entries, obs);
      }, group.options);
      const ioObserved = /* @__PURE__ */ new WeakSet();
      const observe = (el) => {
        if (ioObserved.has(el)) return;
        io.observe(el);
        ioObserved.add(el);
      };
      const unobserve = (el) => {
        if (!ioObserved.has(el)) return;
        io.unobserve(el);
        ioObserved.delete(el);
      };
      for (const e of group.observeTargets) {
        if (e.delegate) {
          newIntersectionDelegates.push({ target: e.target, delegateRoot: e.delegateRoot, observe, unobserve });
          continue;
        }
        if (typeof e.target === "string") {
          for (const r of resolveDelegateRoots(e.delegateRoot)) r.querySelectorAll(e.target).forEach(observe);
          continue;
        }
        observe(e.target);
      }
      st.observers.push(io);
    }
    st.intersectionDelegates.push(...newIntersectionDelegates);
    if (mutationObserverSet.length || newResizeDelegates.length || newIntersectionDelegates.length) {
      if (!st.mo) {
        st.mo = new win.MutationObserver((mutations, obs) => {
          for (const mut of mutations) {
            for (const [nodes, on] of [[mut.addedNodes, true], [mut.removedNodes, false]]) {
              for (const n of Array.from(nodes)) {
                for (const d of st.resizeDelegates) forDelegateMatches(n, d.target, (el) => on ? resizeObserve(el, d.options) : resizeUnobserve(el));
                for (const d of st.intersectionDelegates) forDelegateMatches(n, d.target, (el) => on ? d.observe(el) : d.unobserve(el));
              }
            }
          }
          for (const cb of st.moCallbacks) cb(mutations, obs);
        });
        st.observers.push(st.mo);
      }
      const addObserveTarget = (target, options) => {
        st.moTargets.set(target, mergeMutationInit(st.moTargets.get(target), options));
      };
      for (const e of mutationObserverSet) {
        st.moCallbacks.add(e.callback);
        if (e.delegate) continue;
        const init = e.options ?? { childList: true };
        if (typeof e.target === "string") {
          for (const r of resolveDelegateRoots(e.delegateRoot)) r.querySelectorAll(e.target).forEach((el) => addObserveTarget(el, init));
          continue;
        }
        addObserveTarget(e.target, init);
      }
      for (const d of [...newResizeDelegates, ...newIntersectionDelegates]) {
        for (const r of resolveDelegateRoots(d.delegateRoot)) addObserveTarget(r, { childList: true, subtree: true });
      }
      for (const [target, options] of st.moTargets) st.mo.observe(target, options);
    }
    inst.__swc_observers = st.observers;
  };
  const originalConnected = userCallback(proto.connectedCallback);
  proto.connectedCallback = async function() {
    ensureInit(this);
    const helperHostSet = SwcUtils.getHelperAndHostSet(this);
    this.__swc_helperHostSet = helperHostSet;
    const appHost = helperHostSet.$appHost;
    const useSsr = isSSR(this);
    const gen = this.__swc_connectGen = (this.__swc_connectGen ?? 0) + 1;
    const alive = () => this.__swc_connectGen === gen;
    const obsState = newObserverState();
    try {
      if (appHost && typeof appHost._connected === "function") {
        await appHost._connected(this);
        if (!alive()) return;
      } else if (appHost && typeof appHost) {
        appHost._connected_safari_and_standby ?? (appHost._connected_safari_and_standby = []);
        appHost._connected_safari_and_standby.push(this);
      }
      const conf = getElementConfig(this);
      for (const m of findAllOnConnectedBeforeMetadata(this)) {
        await this._invokeLifecycleMethod(m.propertyKey, helperHostSet);
        if (!alive()) return;
      }
      this._executeSwcScript("swc-on-before-connected", helperHostSet);
      const targetConnectedList = useSsr ? [] : findAllOnConnectedMetadata(constructor);
      const shadowMode = conf?.useShadow || targetConnectedList.find((it) => it.options.useShadow)?.options.useShadow;
      if (shadowMode && !this.shadowRoot) {
        const mode = shadowMode === true ? "open" : shadowMode;
        this.attachShadow({ mode });
      }
      await buildObservers(helperHostSet, "self", obsState, alive);
      if (!alive()) return;
      const stateContext = { ...helperHostSet };
      findAllStateMetadata(this).forEach((it) => {
        stateContext[it.name] = this[it.propertyKey];
      });
      const shadowChildren = [];
      const lightChildren = [];
      if (targetConnectedList.length > 0) {
        const toNodes = (value) => {
          if (!value) return [];
          if (typeof value === "string") {
            const htmlTemplateElement = doc.createElement("template");
            htmlTemplateElement.innerHTML = value;
            return Array.from(htmlTemplateElement.content.childNodes);
          }
          if (Array.isArray(value)) return value;
          return [value];
        };
        const shownFallbacks = [];
        const removeFallbacks = () => shownFallbacks.splice(0).forEach((n) => n.remove());
        for (const meta of targetConnectedList) {
          const useShadowRoot = meta.options.useShadow || conf?.useShadow;
          if (meta.options.fallback) {
            const fb = typeof meta.options.fallback === "function" ? meta.options.fallback(helperHostSet) : meta.options.fallback;
            const fallbackNodes = toNodes(fb);
            const parent = useShadowRoot ? this.shadowRoot : this;
            if (fallbackNodes.length && parent) {
              try {
                const processed = SwcUtils.projectProcessHtml(this._swcId, fallbackNodes, doc);
                parent.append(...processed);
                shownFallbacks.push(...processed);
              } catch (e) {
                console.error("[ElementDefine] fallback setting error:", e);
              }
            }
          }
          let res = await this._invokeLifecycleMethod(meta.propertyKey, helperHostSet);
          if (!alive()) {
            removeFallbacks();
            return;
          }
          if (typeof res === "string") {
            const htmlTemplateElement = doc.createElement("template");
            htmlTemplateElement.innerHTML = res;
            res = htmlTemplateElement.content;
          }
          if (res) {
            const nodes = Array.isArray(res) ? res : [res];
            if (meta.options.useShadow || conf?.useShadow) {
              shadowChildren.push(...nodes);
            } else {
              lightChildren.push(...nodes);
            }
          }
        }
        removeFallbacks();
        try {
          if (this.shadowRoot) {
            const applyShadowChildren = SwcUtils.projectProcessHtml(this._swcId, shadowChildren, doc);
            this.shadowRoot.replaceChildren(...applyShadowChildren);
          }
          if (lightChildren.length) {
            const applyLightChildren = SwcUtils.projectProcessHtml(this._swcId, lightChildren, doc);
            this.replaceChildren(...applyLightChildren);
          }
        } catch (e) {
          console.error("[ElementDefine] innerHTML setting error:", e);
        }
        new ElementApply(this, { id: this._swcId }).apply({ context: stateContext, bind: this });
      } else {
        new ElementApply(this, { id: this._swcId }).apply({ exclude: { html: true, text: true, attribute: true }, context: stateContext, bind: this });
      }
      const root = this.getRootNode();
      if (!globalDelegatedRoots.has(root)) {
        DOM_EVENT_NAMES.forEach((type) => {
          root.addEventListener(type, handleGlobalSwcEvent);
        });
        globalDelegatedRoots.add(root);
      }
      await buildObservers(helperHostSet, "rest", obsState, alive);
      if (!alive()) return;
      if (originalConnected) await originalConnected.apply(this);
      if (!alive()) return;
      for (const m of findAllOnConnectedAfterMetadata(this)) {
        await this._invokeLifecycleMethod(m.propertyKey, helperHostSet);
        if (!alive()) return;
      }
      this._executeSwcScript("swc-on-connected", helperHostSet);
      this._executeSwcScript("swc-on-after-connected", helperHostSet);
      this.__swc_connected = true;
    } finally {
      const aborted = !alive();
      if (!aborted) for (const m of findAllLifecycleMetadata(this, ON_CONNECTED_COMPLETED_METADATA_KEY)) await this._invokeLifecycleMethod(m.propertyKey, helperHostSet);
      if (appHost && typeof appHost._connectedDone === "function") {
        await appHost._connectedDone(this, { aborted });
      }
    }
  };
  const originalDisconnected = userCallback(proto.disconnectedCallback);
  proto.disconnectedCallback = function() {
    this.__swc_connectGen = (this.__swc_connectGen ?? 0) + 1;
    const helperHostSet = connectedHelperHostSet(this);
    const appHost = helperHostSet.$appHost;
    if (appHost && typeof appHost._disconnected === "function") {
      appHost._disconnected(this);
    }
    this._executeSwcScript("swc-on-before-disconnected", helperHostSet);
    for (const m of findAllLifecycleMetadata(this, ON_BEFORE_DISCONNECTED_METADATA_KEY)) this._invokeLifecycleMethod(m.propertyKey, helperHostSet);
    for (const c of cyclers) c.onDisconnected?.(helperHostSet);
    for (const o of this.__swc_observers ?? []) {
      try {
        o?.disconnect?.();
      } catch (e) {
        console.error("[SWC] observer disconnect error:", e);
      }
    }
    this.__swc_observers = [];
    new ElementApply(this, { id: this._swcId }).removeAllEventListener();
    if (originalDisconnected) originalDisconnected.apply(this);
    for (const m of findAllLifecycleMetadata(this, ON_AFTER_DISCONNECTED_METADATA_KEY)) this._invokeLifecycleMethod(m.propertyKey, helperHostSet);
    this._executeSwcScript("swc-on-disconnected", helperHostSet);
    this._executeSwcScript("swc-on-after-disconnected", helperHostSet);
    this.__swc_connected = false;
  };
  const originalAdopted = userCallback(proto.adoptedCallback);
  proto.adoptedCallback = function() {
    const hostSet = SwcUtils.getHostSet(this);
    this._executeSwcScript("swc-on-before-adopted", hostSet);
    for (const m of findAllLifecycleMetadata(this, ON_BEFORE_ADOPTED_METADATA_KEY)) this._invokeLifecycleMethod(m.propertyKey, hostSet);
    for (const c of cyclers) c.onAdopted?.(connectedHelperHostSet(this));
    if (originalAdopted) originalAdopted.apply(this);
    for (const m of findAllLifecycleMetadata(this, ON_AFTER_ADOPTED_METADATA_KEY)) this._invokeLifecycleMethod(m.propertyKey, hostSet);
    this._executeSwcScript("swc-on-adopted", hostSet);
    this._executeSwcScript("swc-on-after-adopted", hostSet);
  };
  const originalAttributeChanged = userCallback(proto.attributeChangedCallback);
  proto.attributeChangedCallback = function(name2, old, newVal) {
    if (originalAttributeChanged) originalAttributeChanged.apply(this, [name2, old, newVal]);
    const helperAndHostSet = SwcUtils.getHelperAndHostSet(this);
    let processedVal = newVal;
    if (newVal !== null) {
      const ae = new ActionExpression(newVal);
      const expr = ae.getFirstExpression("callReturn");
      if (expr) {
        const win2 = SwcUtils.resolveWindow(this);
        const exprHelperAndHostSet = SwcUtils.getHelperAndHostSet(this);
        const script = ConvertUtils.decodeHtmlEntity(expr.script, win2.document);
        try {
          const result = FunctionUtils.executeReturn({
            script,
            context: this,
            args: exprHelperAndHostSet
          });
          processedVal = result;
        } catch (e) {
          console.error(`[SWC] Failed to execute directive {{= ${expr.script} }} on attribute ${name2}: ${exprHelperAndHostSet}`, e);
          processedVal = newVal;
        }
      }
    }
    if (name2.startsWith("swc-on-") && !swcLifecycleAttributes.includes(name2)) {
      if (newVal !== null) {
        const eventName = name2.substring(7);
        this._bindAttributeEvent(this, name2, newVal, eventName);
      }
    }
    for (const c of cyclers) c.onAttributeChanged?.(helperAndHostSet, name2, old, processedVal);
  };
  Object.defineProperty(constructor, "observedAttributes", {
    get: () => mergedObservedAttributes,
    configurable: true
  });
  proto.connectedCallback[SWC_ORIGINAL_CALLBACK] = originalConnected;
  proto.disconnectedCallback[SWC_ORIGINAL_CALLBACK] = originalDisconnected;
  proto.adoptedCallback[SWC_ORIGINAL_CALLBACK] = originalAdopted;
  proto.attributeChangedCallback[SWC_ORIGINAL_CALLBACK] = originalAttributeChanged;
  ReflectUtils.defineMetadata(ELEMENT_CONFIG_KEY, metadata, constructor);
  const registry = metadata.customElementRegistry || win?.customElements;
  if (registry && !registry.get(metadata.name)) {
    registry.define(metadata.name, constructor, metadata.extends ? { extends: metadata.extends } : void 0);
  }
  return constructor;
};
export {
  ELEMENT_CONFIG_KEY,
  elementDefine,
  ensureInit,
  getElementConfig
};
//# sourceMappingURL=elementDefine.js.map
